/* eslint-disable max-lines -- Pre-existing violation */
import {
  Alert,
  Card,
  DataStatus,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  type FormControl,
  type FormSubmitHandler,
  Table,
  TableRow,
  Tooltip,
  openConfirmDialog,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';
import {useWatch, type UseFormReturn} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {
  useArchiveMakeMutation,
  useArchiveModelMutation,
  useCreateModelMutation,
  useGetMakeQuery,
  useListModelQuery,
  useListModelYearQuery,
  useGetMakeOrderStatusMappingQuery,
  useListUnmappedMakeStatusQuery,
  useListMakeQuery,
  useUpdateMakeMutation,
  useUpdateModelMutation,
  type ModelResponseBody,
  type VehicleOrderStatus,
} from '@dms/api/new-vehicle';
import {
  getNewVehicleEnumColorScheme,
  getNewVehicleEnumLabel,
  NewVehiclePageHeader,
  NewVehicleStatusFlag,
  useNewVehicleCatalogueLabels,
} from '@dms/features/new-vehicle';
import {vehiclesRoutes} from '@dms/routes';
import {usePermissions, VehicleStyleSelect} from '@dms/shared';

import {composePath, useNavigate, type ApiError, type Nullish} from 'shared/web';

import {
  optionLabel,
  useVehicleCatalogueMakeOptions,
  useVehicleCatalogueModelOptions,
} from '../hooks/useVehicleCatalogueOptions';
import {findKeyOwner, resolveModelIdentity} from '../utils/catalogueIdentity';
import {
  actionsColumn,
  Cell,
  column,
  handleCatalogueError,
  makeVehicleDataKey,
  modelVehicleDataKey,
  optionalText,
  PASSENGER_CAR,
  RowActions,
  editRowAction,
  archiveRowAction,
  t,
} from '../utils/catalogueUi';
import {OrderStatusMappingNote} from './OrderStatusMappingNote/OrderStatusMappingNote';
import {OrderStatusSignalMappingCard} from './OrderStatusSignalMappingCard/OrderStatusSignalMappingCard';

const buildMakeSchema = (persistedKey: string | Nullish) =>
  z.object({
    name: z.string().min(1),
    hsn: optionalText,
    vehicleDataKey: makeVehicleDataKey(persistedKey),
    version: z.number(),
  });
const buildModelSchema = (makeKey: string | Nullish) =>
  z.object({
    name: z.string().min(1),
    bodyStyle: optionalText,
    modelFamily: optionalText,
    tsn: optionalText,
    vehicleDataKey: modelVehicleDataKey(makeKey, null),
  });
type MakeValues = z.infer<ReturnType<typeof buildMakeSchema>>;
type ModelValues = z.infer<ReturnType<typeof buildModelSchema>>;
// The factory order-status ingress moves a vehicle order only to these stages.
const automaticStages: VehicleOrderStatus[] = ['ordered', 'in_production', 'in_transit'];

function MakeKeyField(props: {
  control: FormControl<MakeValues>;
  makeId: string;
  storedKey: string | Nullish;
  isDisabled: boolean;
}) {
  const catalogue = useVehicleCatalogueMakeOptions(props.storedKey);
  const makes = useListMakeQuery({});
  const picked = useWatch({control: props.control, name: 'vehicleDataKey'});
  const owner =
    picked !== (props.storedKey ?? '')
      ? findKeyOwner(makes.data?.items ?? [], picked, props.makeId)
      : undefined;
  return (
    <FormField
      errorMessage={
        owner
          ? t(
              'entity.newVehicle.catalogue.makeKeyTakenBy',
              '{name} is already linked to this vehicle catalogue make. Link that make instead.',
              {name: owner.name}
            )
          : undefined
      }
      control={props.control}
      data-testid="make-vehicle-data-key"
      name="vehicleDataKey"
      type="choice"
      isSearchable
      isNotClearable
      menuInPortal
      options={catalogue.options}
      isLoading={catalogue.isLoading}
      label={t('entity.newVehicle.catalogue.vehicleCatalogueMake', 'Vehicle catalogue make')}
      helperText={
        catalogue.isError
          ? t(
              'entity.newVehicle.catalogue.vehicleCatalogueUnavailable',
              "The vehicle catalogue can't be loaded right now. You can continue without a link."
            )
          : t(
              'entity.newVehicle.catalogue.makeDataKeyHelper',
              'Leave empty unless this make exists in the shared vehicle catalogue.'
            )
      }
      isDisabled={props.isDisabled}
    />
  );
}

/**
 * The existing model of this make an "Add model" would duplicate or resemble. A duplicate would be
 * refused, so it is offered to open or link; a look-alike needs an explicit link-or-create.
 */
function ExistingModelDecision(props: {
  control: FormControl<ModelValues>;
  models: ModelResponseBody[];
  makeId: string;
  keepSeparate: boolean;
  onOpen: (model: ModelResponseBody) => void;
  onLink: (model: ModelResponseBody, key: string) => void;
  onKeepSeparate: () => void;
}) {
  const [name, vehicleDataKey] = useWatch({
    control: props.control,
    name: ['name', 'vehicleDataKey'],
  });
  const {match, similar} = resolveModelIdentity(props.models, props.makeId, {name, vehicleDataKey});
  if (match?.kind === 'archived') {
    return (
      <Alert
        variant="warning"
        message={t(
          'entity.newVehicle.authoring.modelNameArchived',
          'An archived model of this make is already called {name}. Choose a different name.',
          {name: match.record.name}
        )}
      />
    );
  }
  if (match) {
    return (
      <Alert
        layout="detailed"
        variant="warning"
        data-testid="add-model-existing"
        message={
          match.kind === 'link'
            ? t(
                'entity.newVehicle.catalogue.linkExistingModelQuestion',
                '{name} already exists without a vehicle catalogue link. Link it instead of adding a duplicate?',
                {name: match.record.name}
              )
            : t(
                'entity.newVehicle.catalogue.openExistingModelQuestion',
                '{name} already exists under this make. Open it instead of adding a duplicate?',
                {name: match.record.name}
              )
        }
        actions={[
          match.kind === 'link'
            ? {
                variant: 'secondary',
                title: t('entity.newVehicle.authoring.linkExisting', 'Link {name}', {
                  name: match.record.name,
                }),
                onClick: () => props.onLink(match.record, vehicleDataKey),
              }
            : {
                variant: 'secondary',
                title: t('entity.newVehicle.authoring.useExisting', 'Use {name}', {
                  name: match.record.name,
                }),
                onClick: () => props.onOpen(match.record),
              },
        ]}
      />
    );
  }
  if (!similar.length || props.keepSeparate) {
    return null;
  }
  return (
    <Alert
      layout="detailed"
      variant="warning"
      data-testid="add-model-similar"
      message={t(
        'entity.newVehicle.catalogue.similarModelQuestion',
        'Is {name} this vehicle catalogue model? Link it, or add a new model.',
        {name: similar.map((model) => model.name).join(', ')}
      )}
      actions={[
        ...similar.slice(0, 3).map((model) => ({
          variant: 'secondary' as const,
          title: t('entity.newVehicle.authoring.linkExisting', 'Link {name}', {name: model.name}),
          onClick: () => props.onLink(model, vehicleDataKey),
        })),
        {
          variant: 'secondary' as const,
          title: t('entity.newVehicle.authoring.createNewModel', 'Create new model'),
          onClick: props.onKeepSeparate,
        },
      ]}
    />
  );
}

/** Offers the make's own catalogue model families; a make of the tenant's own has none. */
function NewModelKeyField(props: {
  control: FormControl<ModelValues>;
  formApi: UseFormReturn<ModelValues>;
  makeKey: string | Nullish;
}) {
  const catalogue = useVehicleCatalogueModelOptions(props.makeKey);
  return (
    <FormField
      control={props.control}
      name="vehicleDataKey"
      type="choice"
      isSearchable
      isNotClearable
      menuInPortal
      options={catalogue.options}
      isLoading={catalogue.isLoading}
      label={t('entity.newVehicle.catalogue.vehicleCatalogueModel', 'Vehicle catalogue model')}
      helperText={
        !props.makeKey // eslint-disable-line no-nested-ternary -- Pre-existing violation
          ? t(
              'entity.newVehicle.catalogue.modelKeyNeedsMakeKey',
              'Available once the make is linked to the vehicle catalogue.'
            )
          : catalogue.isError
            ? t(
                'entity.newVehicle.catalogue.vehicleCatalogueModelsUnavailable',
                "The vehicle catalogue can't be loaded right now. You can continue with a model of your own."
              )
            : t(
                'entity.newVehicle.catalogue.modelDataKeyHelper',
                'Leave empty unless this model exists in the shared vehicle catalogue under the same make.'
              )
      }
      isDisabled={!props.makeKey}
      onChange={(value) => {
        // A picked catalogue model names the new model unless the author already typed one.
        const label = typeof value === 'string' && value && optionLabel(catalogue.options, value);
        if (label && !props.formApi.getValues('name').trim()) {
          props.formApi.setValue('name', label, {shouldValidate: true});
        }
      }}
    />
  );
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function MakeDetail() {
  const {getBodyStyle} = useNewVehicleCatalogueLabels();
  const {makeId = ''} = useParams();
  const navigate = useNavigate();
  const [canWrite] = usePermissions({permissionKeys: ['newVehicleCatalogueWrite']});
  const makeQuery = useGetMakeQuery({makeId});
  const modelQuery = useListModelQuery({});
  const modelYearQuery = useListModelYearQuery({});
  const unmappedQuery = useListUnmappedMakeStatusQuery({makeId});
  const mappingQuery = useGetMakeOrderStatusMappingQuery({makeId});
  const [updateMake] = useUpdateMakeMutation();
  const [archiveMake] = useArchiveMakeMutation();
  const [createModel] = useCreateModelMutation();
  const [updateModel] = useUpdateModelMutation();
  const [archiveModel] = useArchiveModelMutation();
  // Omnetic maintains the order-status mapping centrally; a dealer only reads it.
  const mappings = mappingQuery.data?.mappings ?? [];
  const unmappedStatuses = unmappedQuery.data ?? [];
  const [isAddModelOpen, setAddModelOpen] = useState(false);
  const make = makeQuery.data;
  const allModels = modelQuery.data?.items ?? [];
  const models = allModels.filter((model) => model.makeId === makeId);
  const publicationStatus = make?.archived ? 'archived' : 'active';

  const openModel = (modelId: string) =>
    navigate(composePath(vehiclesRoutes.newVehicleCatalogueModel, {params: {modelId}}));

  const saveMake: FormSubmitHandler<MakeValues> = async (values, setErrors, reset) => {
    if (!make) {
      return;
    }
    await updateMake({
      makeId,
      makeWriteRequestBody: {
        name: values.name,
        vehicleDataKey: values.vehicleDataKey || null,
        hsn: values.hsn || null,
        version: values.version,
      },
    })
      .unwrap()
      .then((saved) =>
        reset({
          name: saved.name,
          hsn: saved.hsn ?? '',
          vehicleDataKey: saved.vehicleDataKey ?? '',
          version: saved.version,
        })
      )
      .catch((error: ApiError) => handleCatalogueError(error, setErrors));
  };
  const [isAddModelSeparate, setAddModelSeparate] = useState(false);
  const closeAddModel = () => {
    setAddModelOpen(false);
    setAddModelSeparate(false);
  };
  const linkModel = (model: ModelResponseBody, key: string) =>
    void updateModel({
      modelId: model.id,
      modelWriteRequestBody: {
        makeId: model.makeId,
        name: model.name,
        vehicleDataKey: key,
        version: model.version,
      },
    })
      .unwrap()
      .then(closeAddModel)
      .catch((error: ApiError) => handleCatalogueError(error));
  const addModel: FormSubmitHandler<ModelValues> = async (values, setErrors) => {
    // A duplicate or undecided look-alike is resolved in the dialog, never posted blindly.
    const {match, similar} = resolveModelIdentity(allModels, makeId, values);
    if (match || (similar.length && !isAddModelSeparate)) {
      return;
    }
    await createModel({
      modelWriteRequestBody: {
        makeId,
        name: values.name.trim(),
        vehicleDataKey: values.vehicleDataKey || null,
        modelFamilyGroup: null,
        bodyStyle: values.bodyStyle || null,
        modelFamily: values.modelFamily || null,
        tsn: values.tsn || null,
        version: 1,
      },
    })
      .unwrap()
      .then(closeAddModel)
      .catch((error: ApiError) => handleCatalogueError(error, setErrors));
  };
  const archiveCurrentMake = () => {
    if (!make) {
      return;
    }
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveMake', 'Archive make {name}?', {
        name: make.name,
      }),
      onConfirm: () =>
        void archiveMake({makeId, versionedWriteRequestBody: {version: make.version}})
          .unwrap()
          .catch(handleCatalogueError),
    });
  };

  return (
    <DataStatus
      isLoading={makeQuery.isLoading}
      isError={makeQuery.isError}
      errorMessage={t('entity.newVehicle.errors.makeNotFound', 'Make not found')}
      errorAction={{
        title: t('entity.newVehicle.actions.backToCatalogue', 'Back to catalogue'),
        onClick: () => navigate(vehiclesRoutes.newVehicleCatalogue),
        variant: 'secondary',
      }}
      isEmpty={!make}
    >
      <Show when={make}>
        <VStack spacing={4}>
          <NewVehiclePageHeader
            breadcrumbs={[
              {label: t('page.vehicles.title', 'Vehicles'), href: vehiclesRoutes.home},
              {
                label: t('entity.newVehicle.configurations.title', 'New vehicle configurations'),
                href: vehiclesRoutes.newVehicleConfigurations,
              },
              {
                label: t('entity.newVehicle.catalogue.title', 'Catalogue'),
                href: vehiclesRoutes.newVehicleCatalogue,
              },
              {label: make?.name ?? ''},
            ]}
            title={make?.name ?? ''}
            status={{
              label: getNewVehicleEnumLabel('publicationStatus', publicationStatus, t),
              colorScheme: getNewVehicleEnumColorScheme('publicationStatus', publicationStatus),
              isSubtle: true,
            }}
            parameters={[
              `${t('entity.newVehicle.labels.hsn', 'HSN')} ${make?.hsn ?? '—'}`,
              `${models.length} ${t('entity.newVehicle.catalogue.models', 'Models')}`,
            ]}
            actions={
              canWrite
                ? [
                    {
                      type: 'button',
                      title: t('entity.newVehicle.catalogue.addModel', 'Add model'),
                      leftIcon: 'content/add',
                      onClick: () => setAddModelOpen(true),
                    },
                    {
                      type: 'dropdown-button',
                      title: t('general.labels.more'),
                      variant: 'secondary',
                      rightIcon: 'navigation/expand_more',
                      menuItems: [
                        {
                          label: t('entity.newVehicle.catalogue.archiveMake', 'Archive make'),
                          onClick: archiveCurrentMake,
                        },
                      ],
                    },
                  ]
                : []
            }
          />
          <Grid columns={2} spacing={4}>
            <Card title={t('entity.newVehicle.catalogue.makeDetails', 'Make details')}>
              <Form<MakeValues>
                key={makeId}
                experimentalZodSchema={buildMakeSchema(make?.vehicleDataKey)}
                defaultValues={{
                  name: make?.name ?? '',
                  hsn: make?.hsn ?? '',
                  vehicleDataKey: make?.vehicleDataKey ?? '',
                  version: make?.version ?? 1,
                }}
                onSubmit={saveMake}
              >
                {(control) => (
                  <VStack spacing={3}>
                    <Grid columns={2} spacing={3}>
                      <FormField
                        control={control}
                        name="name"
                        type="text"
                        label={t('entity.vehicle.labels.make', 'Make')}
                        isRequired
                        isDisabled={!canWrite}
                      />
                      <FormField
                        control={control}
                        name="hsn"
                        type="text"
                        label={t('entity.newVehicle.labels.hsn', 'HSN')}
                        isDisabled={!canWrite}
                      />
                      <MakeKeyField
                        control={control}
                        makeId={makeId}
                        storedKey={make?.vehicleDataKey}
                        isDisabled={!canWrite}
                      />
                    </Grid>
                    <Show when={canWrite}>
                      <HStack justify="flex-end">
                        <FormButton
                          control={control}
                          type="submit"
                          title={t('general.actions.save', 'Save')}
                        />
                      </HStack>
                    </Show>
                  </VStack>
                )}
              </Form>
            </Card>
            <Card title={t('entity.newVehicle.catalogue.models', 'Models')}>
              <DataStatus
                isLoading={modelQuery.isLoading || modelYearQuery.isLoading}
                isError={modelQuery.isError || modelYearQuery.isError}
                isEmpty={!models.length}
              >
                <Table
                  columns={[
                    column(t('entity.vehicle.labels.model', 'Model')),
                    column(t('entity.newVehicle.catalogue.bodyStyle', 'Body style'), {
                      width: '30%',
                    }),
                    column(t('entity.newVehicle.catalogue.modelYears', 'Model years'), {
                      width: '20%',
                      align: 'right',
                    }),
                    actionsColumn(),
                  ]}
                >
                  {models.map((model) => (
                    <TableRow key={model.id} onRowClick={() => openModel(model.id)}>
                      <Cell>{model.name}</Cell>
                      <Cell>{model.bodyStyle ? getBodyStyle(model.bodyStyle) : '—'}</Cell>
                      <Cell align="right">
                        {modelYearQuery.data?.items.filter((year) => year.modelId === model.id)
                          .length ?? 0}
                      </Cell>
                      <Cell>
                        <RowActions
                          actions={[
                            editRowAction(() => openModel(model.id)),
                            ...(canWrite
                              ? [
                                  archiveRowAction(() =>
                                    openConfirmDialog({
                                      text: t(
                                        'entity.newVehicle.confirmations.archiveModel',
                                        'Archive model {name}?',
                                        {name: model.name}
                                      ),
                                      onConfirm: () =>
                                        void archiveModel({
                                          modelId: model.id,
                                          versionedWriteRequestBody: {version: model.version},
                                        })
                                          .unwrap()
                                          .catch(handleCatalogueError),
                                    })
                                  ),
                                ]
                              : []),
                          ]}
                        />
                      </Cell>
                    </TableRow>
                  ))}
                </Table>
              </DataStatus>
            </Card>
          </Grid>
          <Card
            data-testid="orderStatusStageMapping"
            title={t(
              'entity.newVehicle.catalogue.importerOrderStatusMapping',
              'Importer order-status mapping'
            )}
          >
            <VStack spacing={3}>
              <Show when={mappingQuery.data}>
                {(data) => <OrderStatusMappingNote integration={data.integration} />}
              </Show>
              <DataStatus
                isLoading={unmappedQuery.isLoading || mappingQuery.isLoading}
                isError={unmappedQuery.isError || mappingQuery.isError}
                isEmpty={!mappings.length && !unmappedStatuses.length}
              >
                <VStack spacing={3}>
                  <Show
                    when={unmappedStatuses.length > 0 && isNotNil(mappingQuery.data?.integration)}
                  >
                    <Text size="small" color="secondary">
                      {t('entity.newVehicle.catalogue.unmappedOrderStatusesNote')}
                    </Text>
                  </Show>
                  <Table
                    columns={[
                      column(t('entity.newVehicle.catalogue.importerStatus', 'Importer status')),
                      column(t('entity.newVehicle.catalogue.usage', 'Usage'), {
                        width: '14%',
                        align: 'right',
                      }),
                      column(t('entity.newVehicle.catalogue.orderStage', 'Order stage'), {
                        width: '30%',
                      }),
                    ]}
                  >
                    {mappings.map((mapping) => (
                      <TableRow key={mapping.rawStatus}>
                        <Cell>
                          <Tooltip
                            label={t(
                              'entity.newVehicle.catalogue.rawImporterStatus',
                              'Raw importer status'
                            )}
                          >
                            <Text size="small">{mapping.rawStatus}</Text>
                          </Tooltip>
                        </Cell>
                        <Cell align="right">{mapping.usageCount}</Cell>
                        <Cell>
                          <HStack spacing={2} align="center" wrap>
                            <NewVehicleStatusFlag group="orderStatus" value={mapping.mappedStage} />
                            <Show when={!automaticStages.includes(mapping.mappedStage)}>
                              <Text size="xSmall" color="secondary">
                                {t(
                                  'entity.newVehicle.catalogue.noAutomaticStage',
                                  'No automatic stage'
                                )}
                              </Text>
                            </Show>
                          </HStack>
                        </Cell>
                      </TableRow>
                    ))}
                    {unmappedStatuses.map((item) => (
                      <TableRow key={item.rawStatus}>
                        <Cell>
                          <Tooltip
                            label={t(
                              'entity.newVehicle.catalogue.rawImporterStatus',
                              'Raw importer status'
                            )}
                          >
                            <Text size="small">{item.rawStatus}</Text>
                          </Tooltip>
                        </Cell>
                        <Cell align="right">{item.usageCount}</Cell>
                        <Cell>
                          <Text size="small" color="secondary">
                            {t('entity.newVehicle.enums.statusLogTarget.unmapped')}
                          </Text>
                        </Cell>
                      </TableRow>
                    ))}
                  </Table>
                </VStack>
              </DataStatus>
            </VStack>
          </Card>
          <OrderStatusSignalMappingCard makeId={makeId} />
          <Dialog
            isOpen={isAddModelOpen}
            title={t('entity.newVehicle.catalogue.addModel', 'Add model')}
            onClose={closeAddModel}
            withAdditionalFooter
          >
            <Form<ModelValues>
              experimentalZodSchema={buildModelSchema(make?.vehicleDataKey)}
              defaultValues={{
                name: '',
                bodyStyle: '',
                modelFamily: '',
                tsn: '',
                vehicleDataKey: '',
              }}
              onSubmit={addModel}
            >
              {(control, formApi) => (
                <VStack spacing={4}>
                  <ExistingModelDecision
                    control={control}
                    models={allModels}
                    makeId={makeId}
                    keepSeparate={isAddModelSeparate}
                    onOpen={(model) => {
                      closeAddModel();
                      openModel(model.id);
                    }}
                    onLink={linkModel}
                    onKeepSeparate={() => setAddModelSeparate(true)}
                  />
                  <Grid columns={2} spacing={3}>
                    <NewModelKeyField
                      control={control}
                      formApi={formApi}
                      makeKey={make?.vehicleDataKey}
                    />
                    <FormField
                      control={control}
                      name="name"
                      type="text"
                      label={t('entity.vehicle.labels.model', 'Model')}
                      isRequired
                    />
                    <VehicleStyleSelect
                      vehicleType={PASSENGER_CAR}
                      control={control}
                      name="bodyStyle"
                      label={t('entity.newVehicle.catalogue.bodyStyle', 'Body style')}
                      isMenuInPortal
                      data-testid="newVehicleCatalogue-addModel"
                    />
                    <FormField
                      control={control}
                      name="modelFamily"
                      type="text"
                      label={t('entity.newVehicle.catalogue.modelFamily', 'Model family')}
                    />
                    <FormField
                      control={control}
                      name="tsn"
                      type="text"
                      label={t('entity.newVehicle.labels.tsn', 'TSN')}
                    />
                  </Grid>
                  <DialogFooter>
                    <HStack justify="flex-end">
                      <FormButton
                        control={control}
                        type="submit"
                        title={t('general.actions.save', 'Save')}
                      />
                    </HStack>
                  </DialogFooter>
                </VStack>
              )}
            </Form>
          </Dialog>
        </VStack>
      </Show>
    </DataStatus>
  );
}
